import {useCallback, useEffect, useState} from 'react';

import type {
    useAutoHideUIInterfaces as useAutoHideUIInterfacesType,
    useFullscreenApi as useFullscreenApiType,
} from '@pexip/hooks';
import {isFullscreen} from '@pexip/hooks';
import type {Signal} from '@pexip/signal';

import type {
    EnableInMeetingUIAutoHideState,
    InMeetingUI,
    MeetingPanelsState,
    MeetingPanelType,
} from '../types';
import {PanelAnimationTypes} from '../utils/meetingPanels';

import {useInMeetingUIState} from './useInMeetingUIState';

export type InMeetingUIHookArgs = {
    enableInMeetingUIAutoHide: Signal<EnableInMeetingUIAutoHideState>;
    useAutoHideUIInterfaces: typeof useAutoHideUIInterfacesType;
    useFullscreenApi: typeof useFullscreenApiType;
    togglePanel: (
        panel: MeetingPanelType,
    ) => (panelsState: MeetingPanelsState) => [boolean, PanelAnimationTypes];
};

export const createInMeetingUIHook =
    (inMeetingUIArgs: InMeetingUIHookArgs) =>
    ({
        isStreamReady,
        isAutoHideUiControlsDisabledByConfig,
        toggleConfirm,
    }: {
        isStreamReady: boolean;
        isAutoHideUiControlsDisabledByConfig: boolean;
        toggleConfirm?: Record<
            MeetingPanelType,
            (togglePanel: () => void) => void
        >;
    }): InMeetingUI => {
        const {
            enableInMeetingUIAutoHide,
            useAutoHideUIInterfaces,
            useFullscreenApi,
            togglePanel,
        } = inMeetingUIArgs;

        const [panelsState, setPanelsState] = useState<MeetingPanelsState>({
            openBreakoutRoomsPanel: false,
            openChatPanel: false,
            openParticipantPanel: false,
            openAddParticipant: false,
            openPluginsPanel: false,
            openTranscriptsPanel: false,
            openMeetingManagerRaisedHand: true,
            openMeetingManagerWaitingInLobby: true,
            openMeetingManagerPlugins: false,
            openMeetingManagerBreakoutRooms: true,
            openMeetingManagerBreakoutRoomsAskingForHelp: true,
            animationType: PanelAnimationTypes.NONE,
        });

        const togglePanelAndPreserveOtherPanelsState = useCallback(
            (key: keyof MeetingPanelsState) => {
                setPanelsState(prevPanelsState => ({
                    ...prevPanelsState,
                    [key]: !prevPanelsState[key],
                }));
            },
            [],
        );

        const togglePanelState = useCallback(
            (panel: MeetingPanelType) => {
                switch (panel) {
                    case 'add':
                        togglePanelAndPreserveOtherPanelsState(
                            'openAddParticipant',
                        );
                        return;
                    case 'meetingManagerRaisedHand':
                        togglePanelAndPreserveOtherPanelsState(
                            'openMeetingManagerRaisedHand',
                        );
                        return;
                    case 'meetingManagerWaitingInLobby':
                        togglePanelAndPreserveOtherPanelsState(
                            'openMeetingManagerWaitingInLobby',
                        );
                        return;
                    case 'meetingManagerPlugins':
                        togglePanelAndPreserveOtherPanelsState(
                            'openMeetingManagerPlugins',
                        );
                        return;
                    case 'meetingManagerBreakoutRooms':
                        togglePanelAndPreserveOtherPanelsState(
                            'openMeetingManagerBreakoutRooms',
                        );
                        return;
                    case 'meetingManagerBreakoutRoomsAskingForHelp':
                        togglePanelAndPreserveOtherPanelsState(
                            'openMeetingManagerBreakoutRoomsAskingForHelp',
                        );
                        return;
                }

                setPanelsState(prevPanelsState => {
                    const [isOpen, animationType] =
                        togglePanel(panel)(prevPanelsState);

                    const visibility = {
                        openAddParticipant: prevPanelsState.openAddParticipant,
                        openMeetingManagerRaisedHand:
                            prevPanelsState.openMeetingManagerRaisedHand,
                        openMeetingManagerWaitingInLobby:
                            prevPanelsState.openMeetingManagerWaitingInLobby,
                        openMeetingManagerPlugins:
                            prevPanelsState.openMeetingManagerPlugins,
                        openMeetingManagerBreakoutRooms:
                            prevPanelsState.openMeetingManagerBreakoutRooms,
                        openMeetingManagerBreakoutRoomsAskingForHelp:
                            prevPanelsState.openMeetingManagerBreakoutRoomsAskingForHelp,
                        openBreakoutRoomsPanel:
                            panel === 'breakoutRooms' && isOpen,
                        openChatPanel: panel === 'chat' && isOpen,
                        openParticipantPanel:
                            panel === 'participants' && isOpen,
                        openPluginsPanel: panel === 'plugins' && isOpen,
                        openTranscriptsPanel: panel === 'transcripts' && isOpen,
                    };

                    return {
                        ...visibility,
                        animationType,
                    };
                });
            },
            [togglePanel, togglePanelAndPreserveOtherPanelsState],
        );

        const togglePanels = useCallback(
            (panel: MeetingPanelType) => {
                // Special handling for panel toggle confirmation
                let requiredConfirm: MeetingPanelType | undefined;
                if (panelsState.openBreakoutRoomsPanel) {
                    requiredConfirm = 'breakoutRooms';
                } else if (panelsState.openChatPanel) {
                    requiredConfirm = 'chat';
                } else if (panelsState.openAddParticipant) {
                    requiredConfirm = 'participants';
                } else if (panelsState.openPluginsPanel) {
                    requiredConfirm = 'plugins';
                } else if (panelsState.openTranscriptsPanel) {
                    requiredConfirm = 'transcripts';
                }

                if (requiredConfirm && toggleConfirm?.[requiredConfirm]) {
                    toggleConfirm[requiredConfirm](() =>
                        togglePanelState(panel),
                    );
                    return;
                }

                // Default handling for all panels
                togglePanelState(panel);
            },
            [
                panelsState.openAddParticipant,
                panelsState.openBreakoutRoomsPanel,
                panelsState.openChatPanel,
                panelsState.openPluginsPanel,
                panelsState.openTranscriptsPanel,
                toggleConfirm,
                togglePanelState,
            ],
        );

        const {enableAutoHide} = useAutoHideUIInterfaces(
            isStreamReady,
            isAutoHideUiControlsDisabledByConfig,
        );

        const showSidePanel =
            panelsState.openParticipantPanel ||
            panelsState.openChatPanel ||
            panelsState.openBreakoutRoomsPanel ||
            panelsState.openPluginsPanel ||
            panelsState.openTranscriptsPanel;

        const {autoHideProps, isAutoHideUIVisible} = useInMeetingUIState(
            showSidePanel,
            enableAutoHide,
            enableInMeetingUIAutoHide,
        );
        const fullscreenApi = useFullscreenApi();

        useEffect(
            () => () => {
                if (isFullscreen(fullscreenApi.fullscreenElement)) {
                    void fullscreenApi.exitFullscreen?.();
                }
            },
            [fullscreenApi],
        );

        return {
            autoHideProps,
            panelsState,
            showSidePanel,
            togglePanels,
            isAutoHideUIVisible,
        };
    };
